<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 px-6 py-4">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Bulk Discounts</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage quantity-based discount tiers</p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                :disabled="isLoading"
                                                @click="loadData"
                                        >
                                                <svg class="w-4 h-4" :class="{ 'animate-spin': isLoading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 px-4 py-2.5 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer hover:bg-orange-6"
                                                @click="showCreateModal = true"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create Bulk Discount
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Search Card -->
                        <div class="bg-white rounded-xl shadow-sm px-6 py-4 mb-4">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-48">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search bulk discounts..."
                                                                class="w-full py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-3">
                                                <label class="text-sm text-gray-700 font-medium">Active:</label>
                                                <select
                                                        v-model="activeFilter"
                                                        class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none min-w-28 focus:border-orange-7"
                                                >
                                                        <option :value="null">All</option>
                                                        <option :value="1">Yes</option>
                                                        <option :value="0">No</option>
                                                </select>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 px-3 py-1 rounded-full font-medium">
                                                        {{ filteredItems.length }} bulk discounts
                                                </span>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading bulk discounts...</span>
                        </div>

                        <!-- Table Card -->
                        <div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="overflow-x-auto">
                                        <table class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50">
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-20">ID</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Name</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-32">Count Type</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-24">Active</th>
                                                                <th class="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-32">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr v-if="paginatedItems.length === 0">
                                                                <td colspan="5" class="text-center py-12 px-4">
                                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                                        </svg>
                                                                        <p class="text-gray-500 text-sm m-0">No bulk discounts found</p>
                                                                        <p class="text-gray-400 text-xs mt-1 mb-0">Create a new bulk discount to get started</p>
                                                                </td>
                                                        </tr>
                                                        <tr
                                                                v-for="item in paginatedItems"
                                                                :key="item.id"
                                                                class="border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                                                                @click="editItem(item.id)"
                                                        >
                                                                <td class="py-3 px-4 text-sm text-gray-500 font-mono">{{ item.id }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-900 font-medium">{{ item.name }}</td>
                                                                <td class="py-3 px-4 text-center">
                                                                        <span
                                                                                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                                                                                :class="item.count === 'pages' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'"
                                                                        >
                                                                                {{ item.count }}
                                                                        </span>
                                                                </td>
                                                                <td class="py-3 px-4 text-center">
                                                                        <span
                                                                                v-if="item.active === 1"
                                                                                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                                                        >
                                                                                Yes
                                                                        </span>
                                                                        <span
                                                                                v-else
                                                                                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500"
                                                                        >
                                                                                No
                                                                        </span>
                                                                </td>
                                                                <td class="py-3 px-4 text-right">
                                                                        <div class="flex justify-end gap-2">
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7"
                                                                                        title="Edit"
                                                                                        @click.stop="editItem(item.id)"
                                                                                >
                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                        </svg>
                                                                                </button>
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-red-600"
                                                                                        title="Delete"
                                                                                        @click.stop="confirmDelete(item)"
                                                                                >
                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                        </svg>
                                                                                </button>
                                                                        </div>
                                                                </td>
                                                        </tr>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="flex justify-between items-center px-6 py-4 border-t border-gray-200">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredItems.length) }} of {{ filteredItems.length }} bulk discounts
                                        </div>
                                        <div class="flex gap-2">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage--"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        type="button"
                                                        class="py-2 px-3 border rounded-md text-sm cursor-pointer min-w-10"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300'"
                                                        @click="currentPage = page"
                                                >
                                                        {{ page }}
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage++"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="showCreateModal = false">
                        <div class="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden">
                                <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">Create Bulk Discount</h3>
                                        <button
                                                type="button"
                                                class="p-1.5 text-gray-400 bg-transparent border-none cursor-pointer rounded-md hover:text-gray-600"
                                                @click="showCreateModal = false"
                                        >
                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <div class="flex flex-col gap-4">
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                                        <input
                                                                v-model="newItem.name"
                                                                type="text"
                                                                placeholder="Enter discount name..."
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Count Type</label>
                                                        <select
                                                                v-model="newItem.count"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                                <option value="pages">Pages</option>
                                                                <option value="items">Items</option>
                                                        </select>
                                                </div>
                                                <div class="flex items-center gap-3">
                                                        <input
                                                                id="active"
                                                                v-model="newItem.active"
                                                                type="checkbox"
                                                                class="w-4 h-4 accent-orange-7"
                                                        >
                                                        <label for="active" class="text-sm text-gray-700">Active</label>
                                                </div>
                                        </div>
                                </div>
                                <div class="px-6 py-4 bg-gray-50 flex justify-end gap-3">
                                        <button
                                                type="button"
                                                class="py-2 px-4 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer hover:bg-gray-50"
                                                @click="showCreateModal = false"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                class="py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer hover:bg-orange-6 disabled:opacity-50"
                                                :disabled="!newItem.name || isCreating"
                                                @click="createItem"
                                        >
                                                {{ isCreating ? 'Creating...' : 'Create' }}
                                        </button>
                                </div>
                        </div>
                </div>

                <!-- Toast Notification -->
                <div
                        v-if="toastMessage"
                        class="fixed bottom-4 right-4 py-3 px-4 rounded-lg shadow-lg text-white text-sm z-50"
                        :class="toastVariant === 'success' ? 'bg-green-600' : 'bg-red-600'"
                >
                        {{ toastMessage }}
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import Swal from 'sweetalert2';
import BulkService from 'services/BulkService';
import type { BulkModel } from '@sosocio/creator/interfaces';

export default defineComponent({
        name: 'BulkDiscountsPage',
        setup() {
                const router = useRouter();
                
                const items = ref<BulkModel[]>([]);
                const isLoading = ref(true);
                const isCreating = ref(false);
                const searchQuery = ref('');
                const activeFilter = ref<number | null>(null);
                const showCreateModal = ref(false);
                const currentPage = ref(1);
                const pageSize = 20;
                
                watch([searchQuery, activeFilter], () => {
                        currentPage.value = 1;
                });
                
                const toastMessage = ref('');
                const toastVariant = ref<'success' | 'error'>('success');
                let toastTimeout: ReturnType<typeof setTimeout>;
                
                const newItem = ref({
                        name: '',
                        count: 'pages',
                        active: true,
                });
                
                const showToast = (message: string, variant: 'success' | 'error' = 'success') => {
                        toastMessage.value = message;
                        toastVariant.value = variant;
                        clearTimeout(toastTimeout);
                        toastTimeout = setTimeout(() => {
                                toastMessage.value = '';
                        }, 3000);
                };
                
                const filteredItems = computed(() => {
                        let result = items.value;
                        
                        if (searchQuery.value.trim()) {
                                const query = searchQuery.value.toLowerCase();
                                result = result.filter(item => 
                                        item.name?.toLowerCase().includes(query) ||
                                        String(item.id).includes(query)
                                );
                        }
                        
                        if (activeFilter.value !== null) {
                                result = result.filter(item => item.active === activeFilter.value);
                        }
                        
                        return result;
                });
                
                const totalPages = computed(() => Math.ceil(filteredItems.value.length / pageSize));
                
                const paginatedItems = computed(() => {
                        const start = (currentPage.value - 1) * pageSize;
                        return filteredItems.value.slice(start, start + pageSize);
                });
                
                const visiblePages = computed(() => {
                        const pages: number[] = [];
                        const total = totalPages.value;
                        const current = currentPage.value;
                        
                        if (total <= 7) {
                                for (let i = 1; i <= total; i++) pages.push(i);
                        } else {
                                if (current <= 4) {
                                        for (let i = 1; i <= 5; i++) pages.push(i);
                                        pages.push(total);
                                } else if (current >= total - 3) {
                                        pages.push(1);
                                        for (let i = total - 4; i <= total; i++) pages.push(i);
                                } else {
                                        pages.push(1);
                                        for (let i = current - 1; i <= current + 1; i++) pages.push(i);
                                        pages.push(total);
                                }
                        }
                        return pages;
                });
                
                const loadData = async () => {
                        isLoading.value = true;
                        try {
                                items.value = await BulkService.getAll(new URLSearchParams({ limit: '0' }));
                        } catch (error: any) {
                                console.error('Failed to load data:', error);
                                showToast('Failed to load bulk discounts', 'error');
                        } finally {
                                isLoading.value = false;
                        }
                };
                
                const editItem = (id: number) => {
                        router.push({ name: 'EditBulkDiscount', params: { id } });
                };
                
                const createItem = async () => {
                        if (!newItem.value.name) return;
                        
                        isCreating.value = true;
                        try {
                                const created = await BulkService.create({
                                        name: newItem.value.name,
                                        count: newItem.value.count,
                                        active: newItem.value.active ? 1 : 0,
                                } as any);
                                items.value.unshift(created);
                                showCreateModal.value = false;
                                newItem.value = {
                                        name: '',
                                        count: 'pages',
                                        active: true,
                                };
                                showToast('Bulk discount created successfully');
                                router.push({ name: 'EditBulkDiscount', params: { id: created.id } });
                        } catch (error: any) {
                                console.error('Failed to create bulk discount:', error);
                                showToast(error.message || 'Failed to create bulk discount', 'error');
                        } finally {
                                isCreating.value = false;
                        }
                };
                
                const confirmDelete = async (item: BulkModel) => {
                        const result = await Swal.fire({
                                title: 'Delete Bulk Discount?',
                                text: `Are you sure you want to delete "${item.name}"?`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#dc2626',
                                confirmButtonText: 'Delete',
                        });
                        
                        if (result.isConfirmed) {
                                try {
                                        await BulkService.delete(item.id);
                                        items.value = items.value.filter(i => i.id !== item.id);
                                        showToast('Bulk discount deleted successfully');
                                } catch (error: any) {
                                        console.error('Failed to delete bulk discount:', error);
                                        showToast(error.message || 'Failed to delete bulk discount', 'error');
                                }
                        }
                };
                
                onMounted(() => {
                        loadData();
                });
                
                return {
                        items,
                        isLoading,
                        isCreating,
                        searchQuery,
                        activeFilter,
                        showCreateModal,
                        currentPage,
                        pageSize,
                        toastMessage,
                        toastVariant,
                        newItem,
                        filteredItems,
                        totalPages,
                        paginatedItems,
                        visiblePages,
                        loadData,
                        editItem,
                        createItem,
                        confirmDelete,
                };
        },
});
</script>
